import { 
  Session, 
  Player, 
  SessionCreationRequest, 
  SessionCreationResponse, 
  ApiResponse 
} from './types';
import { generateId, getDeviceId, getPlatform, getAppVersion, debugLog } from './utils';

export class SessionManager {
  private currentSession: Session | null = null;
  private readonly sdkVersion = '0.1.1';
  private debug: boolean | undefined;
  private apiKey: string;
  private baseUrl: string;
  private platform?: 'ios' | 'android' | 'both';

  constructor(apiKey: string, baseUrl: string, debug?: boolean, platform?: 'ios' | 'android' | 'both') {
    this.apiKey = apiKey;
    this.baseUrl = baseUrl;
    this.debug = debug;
    this.platform = platform;
  }

  async startSession(player: Player): Promise<Session> {
    if (this.currentSession) {
      debugLog(this.debug, 'Session already active, ending previous session');
      await this.endSession();
    }

    const request: SessionCreationRequest = {
      playerId: player.id, // Use internal UUID
      deviceId: getDeviceId(),
      platform: getPlatform(this.platform),
      appVersion: getAppVersion(),
      sdkVersion: this.sdkVersion,
      metadata: {}
    };

    debugLog(this.debug, 'Creating session for player:', player.id);

    try {
      const response = await fetch(`${this.baseUrl}/api/sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`,
          'X-SDK-Version': this.sdkVersion
        },
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const apiResponse: ApiResponse<SessionCreationResponse> = await response.json();
      
      if (!apiResponse.success || !apiResponse.data) {
        throw new Error(apiResponse.error || 'Session creation failed');
      }

      const sessionData = apiResponse.data;
      
      const session: Session = {
        id: sessionData.id,
        sessionToken: sessionData.sessionToken,
        playerId: player.id,
        deviceId: request.deviceId,
        platform: request.platform,
        appVersion: request.appVersion,
        sdkVersion: this.sdkVersion,
        startedAt: new Date(),
        metadata: request.metadata || {}
      };

      this.currentSession = session;
      debugLog(this.debug, 'Started new session:', session.id);
      
      return session;
    } catch (error) {
      debugLog(this.debug, 'Session creation failed:', error);
      throw new Error(`Failed to create session: ${error}`);
    }
  }

  async endSession(): Promise<Session | null> {
    if (!this.currentSession) {
      debugLog(this.debug, 'No active session to end');
      return null;
    }

    const session = { ...this.currentSession };
    session.endedAt = new Date();
    
    debugLog(this.debug, 'Ended session:', session.id);
    this.currentSession = null;
    
    return session;
  }

  getCurrentSession(): Session | null {
    return this.currentSession;
  }

  isSessionActive(): boolean {
    return this.currentSession !== null;
  }
}